import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Link2, FileText, LogOut, LayoutGrid, ExternalLink } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { api, mediaUrl } from "../lib/api";

export default function Intranet() {
  const { t, tr } = useLang();
  const { user, logout } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [quicklinks, setQuicklinks] = useState([]);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/intranet/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/intranet/quicklinks").then((r) => setQuicklinks(r.data)).catch(() => {});
    api.get("/intranet/docs").then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#0B1220]" data-testid="intranet-page">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-light text-sm">NATUR<span className="font-black text-[#5698C7]">COMMODITIES</span> <span className="font-mono-tech text-[10px] text-slate-500 tracking-widest ml-2">INTRANET</span></Link>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && (
              <Link to="/matriz" className="flex items-center gap-2 text-xs font-mono-tech text-[#5698C7] hover:text-[#0B1220] transition-colors duration-200" data-testid="intranet-matriz-link">
                <LayoutGrid size={12} /> {t("matriz_access").toUpperCase()}
              </Link>
            )}
            <span className="text-xs text-slate-500 hidden sm:block">{user?.email}</span>
            <button onClick={logout} className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors duration-200" data-testid="intranet-logout-btn">
              <LogOut size={12} /> {t("logout")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <section className="lg:col-span-2" data-testid="intranet-announcements">
          <div className="flex items-center gap-2 mb-6">
            <Megaphone size={16} className="text-[#5698C7]" strokeWidth={1.5} />
            <h2 className="font-display text-lg font-light">{t("intranet_announcements")}</h2>
          </div>
          <div className="space-y-4">
            {announcements.length === 0 && <p className="text-sm text-slate-400">—</p>}
            {announcements.map((a) => (
              <article key={a._id} className="hud-border bg-white p-6" data-testid={`announcement-${a._id}`}>
                <span className="font-mono-tech text-[10px] text-slate-500 tracking-widest">{a.created_at?.slice(0, 10)}</span>
                <h3 className="font-display text-sm font-medium mt-2 mb-2">{tr(a.title)}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{tr(a.body)}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="space-y-10">
          <section data-testid="intranet-quicklinks">
            <div className="flex items-center gap-2 mb-6">
              <Link2 size={16} className="text-[#5698C7]" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-light">{t("intranet_links")}</h2>
            </div>
            <div className="space-y-2">
              {quicklinks.length === 0 && <p className="text-sm text-slate-400">—</p>}
              {quicklinks.map((l) => (
                <a key={l._id} href={l.url} target="_blank" rel="noreferrer" className="hud-border bg-white p-3 flex items-center justify-between text-xs text-slate-600 hover:text-[#5698C7] transition-colors duration-200" data-testid={`quicklink-${l._id}`}>
                  {l.name} <ExternalLink size={10} />
                </a>
              ))}
            </div>
          </section>

          <section data-testid="intranet-docs">
            <div className="flex items-center gap-2 mb-6">
              <FileText size={16} className="text-[#5698C7]" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-light">{t("intranet_docs")}</h2>
            </div>
            <div className="space-y-2">
              {docs.length === 0 && <p className="text-sm text-slate-400">—</p>}
              {docs.map((d) => (
                <a key={d._id} href={mediaUrl(d.url)} target="_blank" rel="noreferrer" className="hud-border bg-white p-3 block text-xs text-slate-600 hover:text-[#5698C7] transition-colors duration-200" data-testid={`intranet-doc-${d._id}`}>
                  <span className="block font-medium">{tr(d.title) || d.name}</span>
                  {d.description && <span className="text-slate-400">{tr(d.description)}</span>}
                </a>
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
